import { kEurShort } from 'src/app/shared/utils/k-eur-short';
import { toLQPercentString } from 'src/app/shared/utils/to-lq-percent-string';
import { toNumDE } from 'src/app/shared/utils/to-num-de';
import { toPercentString } from 'src/app/shared/utils/to-percent-string';
import { Metric } from '../../../dashboard-config/dashboard-config.model';
import { kpiColumn, KpiMeasure } from '../../../dashboard-config/kpis/kpi-columns';
import { METRIC_PROFILES } from '../../../dashboard-config/metrics/metric-profile-implementations';

/** The measures the bottom-up cards read. */
export type BottomUpMeasure = Extract<KpiMeasure, 'value' | 'prevYear' | 'changePrevYearPct' | 'lq' | 'lqChangePrevYearPct'>;

/** One sidebar metric as the bottom-up cards read it: its total KPI's columns and their formatting. */
export interface BottomUpMetric {
  id: Metric;
  label: string;
  /** 'EUR' for k€ metrics, else the metric's count unit ('Anzahl', 'Personen'). */
  unit: string;
  column(measure: BottomUpMeasure): string;
  format(measure: BottomUpMeasure, value: unknown): string;
}

function toBottomUpMetric(id: Metric): BottomUpMetric | undefined {
  const profile = METRIC_PROFILES[id];
  const label = profile?.labels.label;
  // The metric's first KPI is its total (bottom-up datasets carry no sub-KPIs).
  const kpi = profile?.kpis()[0];
  if (!label || !kpi) return undefined;
  const kEur = kpi.unit === 'kEur';
  const formatValue = (value: unknown) => (kEur ? kEurShort(value as any, 'long') : toNumDE(value as any));
  const formatters: Record<BottomUpMeasure, (value: unknown) => string> = {
    value: formatValue,
    prevYear: formatValue,
    changePrevYearPct: (value) => toPercentString(value as any),
    lq: (value) => `LQ ${Number(value).toFixed(2)}`,
    lqChangePrevYearPct: (value) => toLQPercentString(value as any),
  };
  return {
    id,
    label,
    unit: kEur ? 'EUR' : profile.labels.countUnit ?? 'Anzahl',
    column: (measure) => kpiColumn(kpi, measure),
    format: (measure, value) => formatters[measure](value),
  };
}

const BOTTOM_UP_METRICS = new Map(
  Object.values(Metric)
    .filter((id): id is Metric => typeof id === 'number')
    .flatMap((id) => {
      const metric = toBottomUpMetric(id);
      return metric ? [[id, metric] as const] : [];
    }),
);

/** The bottom-up view of a metric (one instance per metric); undefined for metrics without a sidebar name. */
export function bottomUpMetric(id: Metric): BottomUpMetric | undefined {
  return BOTTOM_UP_METRICS.get(id);
}
